/**
 * Opens a database AS a `@tursodatabase/sync-react-native` `Database` in LOCAL mode
 * (no url, no auth token, no sync engine, no CDC, no `-info`/`-changes` sidecars)
 * and adapts it to the expo-sqlite surface the actor data layer speaks.
 *
 * This is the signed-out actor connection on native. It is the same engine and the
 * same adapter as the signed-in sync connection, so the app runs ONE SQL engine for
 * `actor.db` in both states; only the sync machinery differs.
 *
 * TWO RULES, both measured in Phase 0 of `unified-turso-actor-db-plan.md`:
 *
 *   1. NEVER pass a `url` (or a url-returning function) here. Handing the sync
 *      engine a file that was never connected does NOT adopt it: `connect()` treats
 *      the missing metadata as a clean start, bootstraps the remote and REPLACES the
 *      local content (probe A0/A0b). And a deferred-url file cannot push the
 *      migration DDL its change log captured (probe A3-raw). The signed-out file is
 *      therefore a plain local database, and sign-up moves it aside and replays its
 *      ROWS through a freshly bootstrapped sync connection (probe A10).
 *   2. This file is `actor.db`, the same path the sync open uses. The two never run
 *      at the same time: the actor connection is single-flight and closed before
 *      any mode switch (`closeCachedActorDatabaseConnection`).
 *
 * Returns the adapted `SQLiteDatabase` plus the resolved on-disk path.
 */
import type { SQLiteDatabase } from "@-/com.npmjs.expo-sqlite";
import { adaptTursoToExpoSqlite } from "#turso-adapter";
import { createActorSyncConnectionLock } from "#actor-sync-connection-lock";
import { withSqliteBusyRetry } from "#with-sqlite-busy-retry";
import { openOwnedTursoGeneration } from "#open-owned-turso-generation";
import { updateActorSchema } from "#update-actor-schema";

export interface ActorTursoLocalConnection {
  connection: SQLiteDatabase;
  databasePath: string;
}

export async function openActorTursoLocalConnection(
  databaseName: string,
  options: {
    /** See `ActorTursoSyncOptions.onDamaged`: the connection was abandoned; replace it. */
    onDamaged?: (error: unknown) => void;
    /** See `ActorTursoSyncOptions.onSchemaUpdated`. */
    onSchemaUpdated?: (applied: readonly string[]) => void;
  } = {},
): Promise<ActorTursoLocalConnection> {
  const { Database } = await import("@-/com.npmjs.tursodatabase__sync-react-native");
  const { databasePath, tursoDb } = await openOwnedTursoGeneration(
    databaseName,
    async (path) => new Database({ path }),
    undefined,
    { onDamaged: options.onDamaged },
  );
  // The same updater that migrates a synced replica (see `update-actor-schema.ts`): one
  // transaction, so the database reaches this build's schema or stays where it was. Nothing else
  // holds this engine yet.
  try {
    const update = await updateActorSchema(tursoDb);
    if (update.applied.length > 0) {
      options.onSchemaUpdated?.(update.applied);
    }
  } catch (error) {
    await tursoDb.close();
    throw error;
  }
  // The adapter serialises every statement through a connection lock. With no sync
  // engine there is nothing else to contend with, but the lock also frames drizzle
  // transactions (`begin` … `commit` hold one slot), which the adapter relies on.
  const lock = createActorSyncConnectionLock({
    name: databaseName,
    onDamaged: options.onDamaged,
  });
  const connection = withSqliteBusyRetry(
    adaptTursoToExpoSqlite(tursoDb, databasePath, databaseName, lock, undefined, true),
  );
  return { connection, databasePath };
}
